import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { searchGovOps } from '../api/govopsMunicipalService';
import { formatGovOpsError } from '../api/govopsErrors';

const STORAGE_KEY = 'rhs_global_search_q';

/**
 * GOVOPS-1303 — org-wide search across permits, cases, violations.
 */
export default function GlobalSearch({ className = '' }) {
  const [q, setQ] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (_) {
      return '';
    }
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const runSearch = useCallback(async (query) => {
    const trimmed = (query || '').trim();
    if (!trimmed) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const rows = await searchGovOps(trimmed);
      setResults(rows);
    } catch (err) {
      setError(formatGovOpsError(err));
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        sessionStorage.setItem(STORAGE_KEY, q);
      } catch (_) {
        /* ignore */
      }
      runSearch(q);
    }, 300);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  return (
    <div className={`rhs-global-search ${className}`.trim()} role="search">
      <label className="rhs-sr-only" htmlFor="rhs-global-search-input">
        Search permits, cases, and violations
      </label>
      <input
        id="rhs-global-search-input"
        type="search"
        className="rhs-input"
        placeholder="Search permits, cases, violations…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
      />
      {loading && <p className="rhs-muted rhs-global-search__status">Searching…</p>}
      {error && (
        <p className="rhs-global-search__error" role="alert">
          {error}
        </p>
      )}
      {!loading && q.trim() && results.length === 0 && !error && (
        <p className="rhs-muted rhs-global-search__status">No matches.</p>
      )}
      {results.length > 0 && (
        <ul className="rhs-global-search__results">
          {results.map((row) => (
            <li key={`${row.type}-${row.id}`}>
              <Link to={row.href} className="rhs-global-search__link">
                <span className="rhs-badge rhs-badge--sm">{row.type}</span>
                <strong>{row.title}</strong>
                {row.subtitle && <span className="rhs-muted"> — {row.subtitle}</span>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
